import { forwardRef, useMemo, type ReactNode } from "react";
import { SelectorAvatarStack } from "./selector-avatar";
import {
  SelectorPlaceholder,
  SelectorTrigger,
  SelectorTriggerLabel,
} from "./selector-trigger";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { cn } from "../../../lib/utils";
import type {
  SelectorAccount,
  SelectorGroup,
  SelectorTriggerProps,
} from "./types";

export interface AccountMultiSelectProps extends SelectorTriggerProps {
  accounts: SelectorAccount[];
  selectedIds: string[];
  groups?: SelectorGroup[];
  selectedGroup?: SelectorGroup;
  summary?: ReactNode;
  maxVisibleAccounts?: number;
  /** Display account avatars without a selector or editing controls. */
  readOnly?: boolean;
}

export const AccountMultiSelect = forwardRef<
  HTMLButtonElement,
  AccountMultiSelectProps
>(function AccountMultiSelect(
  {
    accounts,
    groups = [],
    selectedIds,
    selectedGroup,
    summary,
    maxVisibleAccounts = 3,
    readOnly = false,
    placeholder = "Select accounts",
    size = "md",
    ...props
  },
  ref
) {
  const selectedAccounts = useMemo(() => {
    const selected = new Set(selectedIds);
    return accounts.filter((account) => selected.has(account.id));
  }, [accounts, selectedIds]);

  const inferredGroup = useMemo(() => {
    if (selectedGroup || selectedIds.length === 0) return selectedGroup;
    const selected = new Set(selectedIds);
    return groups.find((group) => {
      const groupIds = (group.accounts || []).map((account) => account.id);
      return (
        groupIds.length === selected.size &&
        groupIds.every((id) => selected.has(id))
      );
    });
  }, [groups, selectedGroup, selectedIds]);

  const faceCap = inferredGroup
    ? Math.min(4, maxVisibleAccounts)
    : maxVisibleAccounts;
  // Sliced so the stack never renders a chip of its own: two counters with
  // different starting points made the displayed number shrink as the
  // selection grew.
  const faces = selectedAccounts.slice(0, faceCap);

  if (readOnly) {
    if (selectedAccounts.length === 0) return null;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="group"
            tabIndex={0}
            aria-label={props["aria-label"] ?? "Selected accounts"}
            className={cn(
              "inline-flex shrink-0 cursor-default items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blurple-500",
              props.className
            )}
          >
            <SelectorAvatarStack
              accounts={selectedAccounts}
              max={faceCap}
              size={20}
            />
            <span className="sr-only">
              {selectedAccounts.map((account) => account.label).join(", ")}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {inferredGroup ? <div>{inferredGroup.label}</div> : null}
          {selectedAccounts.map((account) => (
            <div key={account.id}>{account.label}</div>
          ))}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <SelectorTrigger ref={ref} placeholder={placeholder} size={size} {...props}>
      {selectedAccounts.length === 0 ? (
        <SelectorPlaceholder>{placeholder}</SelectorPlaceholder>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <SelectorAvatarStack accounts={faces} max={faceCap} size={24} />
          <SelectorTriggerLabel
            className="text-neutral-800"
            suffix={
              selectedAccounts.length > 1 ? (
                <span
                  aria-hidden
                  className="ml-1.5 text-neutral-500 tabular-nums"
                >
                  {`· ${selectedAccounts.length}`}
                </span>
              ) : undefined
            }
          >
            {inferredGroup ? inferredGroup.label : selectedAccounts[0].label}
          </SelectorTriggerLabel>
          <span className="sr-only">
            {selectedAccounts.length === 1
              ? "1 account selected"
              : `${selectedAccounts.length} accounts selected`}
          </span>
          {summary ? <span className="ml-auto shrink-0">{summary}</span> : null}
        </span>
      )}
    </SelectorTrigger>
  );
});
